/**
 * DIMENSIONS MAPPER - DEPRECATED (Star Schema INT32)
 * ⚠️ IMPORTANT: With INT32 star schema, SQL JOINs already return human-readable names!
 * This file is kept for backward compatibility but should NOT be used for new code.
 * 
 * BEFORE (UUID approach): agentName = UUID → Frontend maps to "John Doe"
 * NOW (INT32 approach): agentName = "John Doe" (already mapped by SQL JOIN)
 */

/**
 * Map agent UUID to agent display name
 * ⚠️ DEPRECATED: SQL now returns names directly via JOINs
 * @param {string} agentKey - Agent UUID or name
 * @param {Array} agents - Array from Redux store (state.reducer.AGENTS)
 * @returns {string} Agent display name
 */
export function mapAgentName(agentKey, agents) {
    // If already a name (not a UUID), return as-is
    if (!agentKey) return 'N/A';

    // Check if it's already a human-readable name (contains space or non-UUID characters)
    if (agentKey.includes(' ') || !agentKey.match(/^[0-9a-f-]+$/i)) {
        return agentKey;
    }

    // Legacy UUID mapping (for old data)
    if (!agents) return agentKey;

    const agent = agents.find(a => a.AGENT_ID === agentKey);
    if (agent) {
        return agent.AGENT_DISPLAYNAME;
    } else {
        return agentKey;
    }
}

/**
 * Map flow UUID to flow name
 * ⚠️ DEPRECATED: SQL now returns names directly via JOINs
 * @param {string} flowKey - Flow UUID or name
 * @param {Array} flows - Array from Redux store (state.reducer.FLOWS)
 * @returns {string} Flow name
 */
export function mapFlowName(flowKey, flows) {
    if (!flowKey) return 'N/A';

    // If already a name, return as-is
    if (flowKey.includes(' ') || !flowKey.match(/^[0-9a-f-]+$/i)) {
        return flowKey;
    }

    // Legacy UUID mapping
    if (!flows) return flowKey;
    const flow = flows.find(f => f.FLOW_ID === flowKey);
    return flow?.FLOW_NAME || flowKey;
}

/**
 * Map queue UUID to queue name
 * ⚠️ DEPRECATED: SQL now returns names directly via JOINs
 * @param {string} queueKey - Queue UUID or name
 * @param {Array} queues - Array from Redux store (state.reducer.QUEUES)
 * @returns {string} Queue name
 */
export function mapQueueName(queueKey, queues) {
    if (!queueKey) return 'N/A';

    // If already a name, return as-is
    if (queueKey.includes(' ') || !queueKey.match(/^[0-9a-f-]+$/i)) {
        return queueKey;
    }

    // Legacy UUID mapping
    if (!queues) return queueKey;
    const queue = queues.find(q => q.QUEUE_ID === queueKey);
    return queue?.QUEUE_NAME || queueKey;
}

/**
 * Map all dimensions in analytics data row
 * ⚠️ DEPRECATED: With INT32 star schema, names are already resolved by SQL
 * This function now only maps if values are UUIDs (legacy data)
 * @param {Object} row - Analytics data row
 * @param {Object} dimensions - Object with { agents, flows, queues } from Redux
 * @returns {Object} Row with mapped names
 */
export function mapAnalyticsRow(row, dimensions) {
    if (!row) return row;

    const { agents = [], flows = [], queues = [] } = dimensions || {};

    return {
        ...row,
        // Only map if value looks like a UUID (backward compatibility)
        agentName: mapAgentName(row.agentName, agents),
        flowName: mapFlowName(row.flowName, flows),
        queueName: mapQueueName(row.queueName, queues)
    };
}

/**
 * Map all dimensions in analytics data array
 * ⚠️ DEPRECATED: With INT32 star schema, SQL JOINs already return names
 * This function is kept for backward compatibility only
 * @param {Array} data - Array of analytics rows
 * @param {Object} dimensions - Object with { agents, flows, queues } from Redux
 * @returns {Array} Data with mapped names (or as-is if already mapped)
 */
export function mapAnalyticsData(data, dimensions) {
    if (!data) {
        return null;
    }

    // Check if this is already transformed metric data (has value/trend)
    if (!Array.isArray(data) && typeof data === 'object' && (data.value !== undefined || data.trend !== undefined)) {
        return data;
    }

    if (!Array.isArray(data)) {
        // Check if backend returned { data: [...] } structure
        if (data.data && Array.isArray(data.data)) {
            return data.data.map(row => mapAnalyticsRow(row, dimensions));
        }

        // Check if backend returned { result: [...] } structure
        if (data.result && Array.isArray(data.result)) {
            return data.result.map(row => mapAnalyticsRow(row, dimensions));
        }

        return data;
    }

    const mappedData = data.map(row => mapAnalyticsRow(row, dimensions));
    return mappedData;
}

/**
 * Translate status/legend labels from English to translation keys
 * Maps common status values to translation keys
 * @param {string} label - Original label (e.g., "answered", "abandoned")
 * @returns {string} Translation key (e.g., "ANSWERED", "ABANDONED")
 */
export function getLabelTranslationKey(label) {
    if (!label) return label;

    const labelLower = label.toString().toLowerCase();

    // Status translations
    const statusMap = {
        'answered': 'ANSWERED',
        'abandoned': 'ABANDONED',
        'unanswered': 'UNANSWERED',
        'unknown': 'UNKNOWN',
        'répondus': 'ANSWERED',
        'abandonnés': 'ABANDONED',
        'non répondus': 'UNANSWERED',
        'inconnu': 'UNKNOWN',
        // Response time distribution categories
        'fast': 'RESPONSE_FAST_LABEL',
        'standard': 'RESPONSE_NORMAL_LABEL',
        'slow': 'RESPONSE_SLOW_LABEL',
        'rapide': 'RESPONSE_FAST_LABEL',
        'normal': 'RESPONSE_NORMAL_LABEL',
        'lent': 'RESPONSE_SLOW_LABEL'
    };

    return statusMap[labelLower] || label;
}
